import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useI18n } from '../contexts/I18nContext'
import LanguageToggle from '../components/ui/LanguageToggle'
import { LogIn, Eye, EyeOff, AlertCircle } from 'lucide-react'

export default function LoginPage() {
  const { signIn, resetPassword } = useAuth()
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [resetSent, setResetSent] = useState(false)
  const [mode, setMode] = useState('login') // 'login' | 'reset'

  const handleLogin = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await signIn(email, password)
    } catch (err) {
      setError(
        err.message === 'Invalid login credentials'
          ? t('auth.invalidCredentials')
          : err.message
      )
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await resetPassword(email)
      setResetSent(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-warm-100 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <img src="/logo-square.png" alt="Chatrone" className="w-12 h-12 rounded-xl shadow-sm" />
          </div>
          <h1 className="font-display text-2xl font-bold text-warm-900">Chatrone</h1>
        </div>

        <div className="absolute top-4 right-4">
          <LanguageToggle />
        </div>

        <div className="card p-6">
          {mode === 'login' ? (
            <>
              <h2 className="font-display text-lg font-semibold text-warm-900 mb-1">{t('auth.signIn')}</h2>
              <p className="text-sm text-warm-500 mb-5">{t('auth.enterCredentials')}</p>

              {error && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="input-label">{t('auth.email')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@chatrone.com"
                    className="input-field"
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="input-label">{t('auth.password')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="input-field pr-10"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-400 hover:text-warm-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center py-2.5"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <LogIn size={16} />
                      {t('auth.signIn')}
                    </>
                  )}
                </button>
              </form>

              <button
                onClick={() => { setMode('reset'); setError(null) }}
                className="w-full text-center text-sm text-warm-500 hover:text-navy-500 mt-4 transition-colors"
              >
                {t('auth.forgotPassword')}
              </button>
            </>
          ) : (
            <>
              <h2 className="font-display text-lg font-semibold text-warm-900 mb-1">{t('auth.resetPassword')}</h2>
              <p className="text-sm text-warm-500 mb-5">{t('auth.resetDescription')}</p>

              {error && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {resetSent ? (
                <div className="p-4 bg-sage-50 border border-sage-200 rounded-lg">
                  <p className="text-sm text-sage-700">
                    {t('auth.checkEmail')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleReset} className="space-y-4">
                  <div>
                    <label className="input-label">{t('auth.email')}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@chatrone.com"
                      className="input-field"
                      required
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center py-2.5"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      t('auth.sendResetLink')
                    )}
                  </button>
                </form>
              )}

              <button
                onClick={() => { setMode('login'); setError(null); setResetSent(false) }}
                className="w-full text-center text-sm text-warm-500 hover:text-navy-500 mt-4 transition-colors"
              >
                {t('auth.backToSignIn')}
              </button>
            </>
          )}
        </div>

        <p className="text-center text-xs text-warm-400 mt-6">
          Chatrone Management &middot; Chatrone Production &middot; Chatrone Latin America
        </p>
      </div>
    </div>
  )
}
